if (!customElements.get('tabbed-content')) {
  class Tabs extends HTMLElement {
    constructor() {
      super();
      this.tabList = this.querySelector('[role="tablist"]');
      this.activeTab = this.tabList.querySelector('[aria-selected="true"]');
      this.isVerticalTablist = this.tabList.getAttribute('aria-orientation') === 'vertical';
      this.tabs = this.querySelectorAll('[role="tab"]');
      this.panels = this.querySelectorAll('[role="tabpanel"]');
      this.navPrevBtn = this.querySelector('.slider-nav__btn[name="prev"]');
      this.navNextBtn = this.querySelector('.slider-nav__btn[name="next"]');

      // If no tab is active by default, activate the first tab.
      if (!this.activeTab) {
        this.activeTab = this.tabs[0];
        this.activateTab(this.activeTab);
      }

      this.addListeners();
      this.updateNavButtonStates();
    }

    addListeners() {
      this.tabList.addEventListener('click', this.handleClick.bind(this));
      this.tabList.addEventListener('keydown', this.handleKeydown.bind(this));

      // Optional prev/next nav buttons (e.g. a carousel-slider paired with the tablist)
      // switch the active tab, without wraparound, in addition to any scrolling they
      // already perform.
      if (this.navPrevBtn) {
        this.navPrevBtn.addEventListener('click', () => this.stepTab(-1));
      }
      if (this.navNextBtn) {
        this.navNextBtn.addEventListener('click', () => this.stepTab(1));
      }

      // The paired carousel-slider also toggles `disabled` on these buttons based on
      // its own scroll position. That should defer to the active tab instead: the
      // prev button is disabled on the first tab, the next button on the last tab,
      // regardless of scroll position. Re-sync whenever something else changes it.
      if (this.navPrevBtn || this.navNextBtn) {
        this.navButtonObserver = new MutationObserver(() => this.updateNavButtonStates());
        this.observeNavButtons();
      }
    }

    /**
     * Starts observing the prev/next nav buttons for external `disabled` changes
     * (e.g. from the paired carousel-slider). Separated from the MutationObserver
     * construction so updateNavButtonStates() can briefly disconnect/reconnect
     * around its own writes to those same buttons — otherwise the observer would
     * react to its own mutations (DOM attribute mutations fire even when the new
     * value matches the old one) and loop forever.
     */
    observeNavButtons() {
      [this.navPrevBtn, this.navNextBtn].forEach((btn) => {
        if (btn) this.navButtonObserver.observe(btn, { attributes: true, attributeFilter: ['disabled'] });
      });
    }

    /**
     * Activates the tab adjacent to the current one, without wrapping around.
     * Used by the prev/next nav buttons (as opposed to keyboard arrow navigation
     * within the tablist itself, which wraps per standard tab UX).
     * @param {number} direction - -1 for previous, 1 for next.
     */
    stepTab(direction) {
      const tabs = Array.from(this.tabs);
      const nextIndex = tabs.indexOf(this.activeTab) + direction;
      if (nextIndex < 0 || nextIndex > tabs.length - 1) return;
      this.activateTab(tabs[nextIndex]);
    }

    /**
     * Disables the prev nav button on the first tab and the next nav button on the
     * last tab, based on which tab is currently active.
     */
    updateNavButtonStates() {
      if (!this.navPrevBtn && !this.navNextBtn) return;
      const tabs = Array.from(this.tabs);
      const currentIndex = tabs.indexOf(this.activeTab);

      // Disconnect while writing so the observer doesn't react to its own writes
      // (a same-value attribute write still queues a mutation record) and loop.
      if (this.navButtonObserver) this.navButtonObserver.disconnect();
      if (this.navPrevBtn) this.navPrevBtn.disabled = currentIndex <= 0;
      if (this.navNextBtn) this.navNextBtn.disabled = currentIndex >= tabs.length - 1;
      if (this.navButtonObserver) this.observeNavButtons();
    }

    /**
     * Handles 'click' events on the tablist.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      const tab = evt.target.closest('[role="tab"]');
      if (!tab || tab === this.activeTab) return;
      this.activateTab(tab);
    }

    /**
     * Handles 'keydown' events on the tablist.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      switch (evt.key) {
        case 'ArrowLeft':
        case 'ArrowRight':
          evt.preventDefault();
          if (!this.isVerticalTablist) {
            this.switchTabOnKeyPress(evt.key);
          }
          break;

        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();
          if (this.isVerticalTablist) {
            this.switchTabOnKeyPress(evt.key);
          }
          break;

        // Activate the first tab.
        case 'Home':
          evt.preventDefault();
          this.activateTab(this.tabs[0]);
          break;

        // Activate the last tab.
        case 'End':
          evt.preventDefault();
          this.activateTab(this.tabs[this.tabs.length - 1]);
          break;
      }
    }

    /**
     * Activates the next or previous tab according to the key pressed.
     * @param {string} key - Key pressed.
     */
    switchTabOnKeyPress(key) {
      // Index-based lookup (rather than DOM sibling traversal) so this keeps working
      // even when each tab button is wrapped in its own element (e.g. a slider item).
      const tabs = Array.from(this.tabs);
      const currentIndex = tabs.indexOf(this.activeTab);

      if (key === 'ArrowRight' || key === 'ArrowDown') {
        const nextIndex = currentIndex === tabs.length - 1 ? 0 : currentIndex + 1;
        this.activateTab(tabs[nextIndex]);
      } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
        const prevIndex = currentIndex === 0 ? tabs.length - 1 : currentIndex - 1;
        this.activateTab(tabs[prevIndex]);
      }
    }

    /**
     * Activates a tab.
     * @param {Element} tab - Tab element (button).
     */
    activateTab(tab) {
      this.deactivateActiveTab();

      Tabs.setTabState(tab, true);
      tab.removeAttribute('tabindex');
      this.activeTab = tab;
      this.updateNavButtonStates();

      if (document.activeElement.matches('.tablist__tab')) {
        tab.focus();
      }
    }

    /**
     * Deactivates the currently active tab.
     */
    deactivateActiveTab() {
      Tabs.setTabState(this.activeTab, false);
      this.activeTab.setAttribute('tabindex', '-1');
      this.activeTab = null;
    }

    /**
     * Sets the active state of a tab and associated content panel.
     * @param {Element} tab - Tab element (button).
     * @param {boolean} active - Set the tab as active.
     */
    static setTabState(tab, active) {
      tab.setAttribute('aria-selected', active);

      const panelId = tab.getAttribute('aria-controls');
      document.getElementById(panelId).hidden = !active;
    }
  }

  customElements.define('tabbed-content', Tabs);
}
